import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronDown, ChevronUp, ChevronLeft, ChevronRight, CalendarDays, Clock, Gift,
} from 'lucide-react';
import { supabase } from '../config/supabase';
import supabaseService from '../services/supabaseService';
import '../assets/styles/booking_form.css';

const DAYS_PER_PAGE = 14;

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

// February gets 29: only a day and a month are stored, so there is no year to
// say whether this particular one is a leap year. Somebody born on the 29th has
// to be able to say so.
const DAYS_IN_MONTH = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

// Where an in-progress booking waits while the browser is at Google. Session
// storage, not local: it belongs to this tab and this attempt only.
const RESUME_KEY = 'bbv.booking-in-progress';

// An hour. Long enough for a slow sign-in on a phone, short enough that a
// booking abandoned this morning is never silently resurrected this afternoon
// with a slot that has since gone.
const RESUME_MAX_AGE_MS = 60 * 60 * 1000;

const toISODate = (d) => {
  // Local date, not UTC — toISOString() would roll the date over in NZ evenings.
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const formatDayLabel = (iso) => {
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString('en-NZ', { weekday: 'long', day: 'numeric', month: 'long' });
};

const formatSlotTime = (time) => {
  const [h, m] = time.split(':');
  const hour = parseInt(h, 10);
  const period = hour >= 12 ? 'pm' : 'am';
  return `${hour % 12 || 12}:${m} ${period}`;
};

// Money is integer cents everywhere. Whole dollars lose the ".00" — "$85", not
// "$85.00" — to match how prices are written on the rest of the site.
const formatMoney = (cents) => {
  const value = (Number(cents) || 0) / 100;
  return value % 1 === 0 ? `$${value}` : `$${value.toFixed(2)}`;
};

// "2 August", and "2 August 2025" once it is old enough for the year to matter.
const formatVisitDate = (value) => {
  if (!value) return '';
  const d = new Date(`${String(value).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(d.getTime())) return '';
  const options = { day: 'numeric', month: 'long' };
  if (d.getFullYear() !== new Date().getFullYear()) options.year = 'numeric';
  return d.toLocaleDateString('en-NZ', options);
};

/* ── holding a booking across the Google redirect ───────────────────────── */
// signInWithOAuth hands the whole browser to Google. Everything the client has
// chosen is React state and does not survive that, so it is written down first
// and read back on the way in. Only the choices — no name, email or phone: those
// come back from the account they just signed into, and there is no reason to
// leave anyone's contact details sitting in storage.
//
// Session storage is unavailable in some privacy modes. Nothing here is
// important enough to break booking over, so every access is defensive.

const writeResume = (payload) => {
  try {
    window.sessionStorage.setItem(RESUME_KEY, JSON.stringify(payload));
  } catch (err) {
    /* the client simply picks their time again — annoying, not broken */
  }
};

const clearResume = () => {
  try {
    window.sessionStorage.removeItem(RESUME_KEY);
  } catch (err) {
    /* as above */
  }
};

// Read and clear in one go: a restore happens once, on the way back from Google.
// Leaving the key behind would resurrect a stale booking on a later visit.
const readResume = () => {
  let raw = null;
  try {
    raw = window.sessionStorage.getItem(RESUME_KEY);
    if (raw) window.sessionStorage.removeItem(RESUME_KEY);
  } catch (err) {
    return null;
  }
  if (!raw) return null;

  try {
    const saved = JSON.parse(raw);
    if (!saved || saved.v !== 1) return null;
    if (!Array.isArray(saved.treatmentIds) || saved.treatmentIds.length === 0) return null;
    if (!saved.slot || !saved.slot.slot_date || !saved.slot.slot_time) return null;
    if (!Number.isFinite(saved.savedAt)) return null;
    if (Date.now() - saved.savedAt > RESUME_MAX_AGE_MS) return null;
    return saved;
  } catch (err) {
    return null;
  }
};

/* ── signing in ─────────────────────────────────────────────────────────── */

// Google's brand guidelines require their own mark on the button, and no page on
// this site fetches anything from a third party, so it is inlined.
function GoogleMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true" focusable="false">
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.28-3.14.76-4.59l-7.97-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
    </svg>
  );
}

// Auth is the one thing components touch directly, and this needs a different
// landing page from supabaseService.signInWithGoogle(): somebody signing in to
// book has to come back to the booking page, not to /account.
//
// Two looks. "plain" is Google's white button, used where it sits inside a panel
// that already has a heading doing the persuading. "primary" is the filled one
// the gate opens with — the mark keeps its white background there, as a tile, so
// the multicolour G is never drawn straight onto a colour.
function GoogleButton({
  label, redirectPath, variant = 'plain', onBeforeGoogle, onGoogleFailed, onError,
}) {
  const [busy, setBusy] = useState(false);

  const start = async () => {
    if (busy) return;
    setBusy(true);
    onError('');

    try {
      if (onBeforeGoogle) onBeforeGoogle();
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: { redirectTo: `${window.location.origin}${redirectPath}` },
      });
      if (error) throw new Error(error.message);
    } catch (err) {
      // Still here, so nothing was handed over and any note we left ourselves is
      // now misleading.
      if (onGoogleFailed) onGoogleFailed();
      setBusy(false);
      // Plumbing that failed rather than a person who failed you, so nobody
      // claims it in the first person.
      onError(
        'The Google sign-in page wouldn\'t open. Have another go, or use your email and password '
        + 'instead.',
      );
    }
  };

  return (
    <button
      type="button"
      className={`bf-google${variant === 'primary' ? ' bf-google--primary' : ''}`}
      onClick={start}
      disabled={busy}
    >
      {variant === 'primary'
        ? <span className="bf-google-mark"><GoogleMark /></span>
        : <GoogleMark />}
      {busy ? 'Taking you to Google…' : label}
    </button>
  );
}

// The email half of signing in, shared by the gate and by the offer on the
// confirmation screen. It is a real <form> in both places — neither is inside
// the booking form — so Enter does the obvious thing and never submits a
// booking behind it.
//
// A password sign-in resolves right here. The parent is watching auth state and
// takes this away, then fills the blanks from the client's profile — deliberately
// not from what was typed here, so signing in as one person while booking for
// another does not quietly rewrite the booking's email address.
function EmailPasswordForm({ mode, onModeChange, idPrefix, prefill, emailHint, onDetails, onPending }) {
  const [form, setForm] = useState(() => ({
    name: prefill.name || '',
    phone: prefill.phone || '',
    email: prefill.email || '',
    password: '',
  }));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const emailRef = useRef(null);

  // This only ever mounts because somebody tapped "Sign in with email", so
  // taking the caret is what was asked for. It never appears underneath anyone
  // who is already typing.
  useEffect(() => {
    emailRef.current?.focus();
  }, []);

  const change = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;

    const email = form.email.trim();
    const name = form.name.trim();
    const phone = form.phone.trim();

    // Said here rather than letting Supabase answer with something written for a
    // developer.
    if (!email || !form.password) {
      setError('Please fill in your email address and a password.');
      return;
    }
    if (mode === 'signup' && form.password.length < 8) {
      setError('Please choose a password of at least 8 characters.');
      return;
    }

    setBusy(true);
    setError('');
    setNotice('');

    try {
      if (mode === 'signup') {
        const result = await supabaseService.signUp({
          email, password: form.password, fullName: name, phone,
        });
        // Whether or not a session came back, these are their details and the
        // booking form should stop asking for them again.
        if (onDetails) onDetails({ name, email, phone });

        if (!result?.session) {
          const message = 'Almost there — check your email and click the link to confirm your '
            + "account. You don't have to wait: carry on booking now.";
          // The gate hands the message onward and gets out of the way, because
          // nobody should be stuck behind a confirmation email they have to go
          // and find. Everywhere else it is shown in place.
          if (onPending) onPending(message);
          else setNotice(message);
        }
      } else {
        await supabaseService.signIn({ email, password: form.password });
      }
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="bf-auth-fields" onSubmit={submit} noValidate>
      {mode === 'signup' && (
        <>
          <div className="form-field">
            <label htmlFor={`${idPrefix}-auth-name`}>Your name</label>
            <input
              id={`${idPrefix}-auth-name`} name="name" type="text" autoComplete="name"
              value={form.name} onChange={change} placeholder="Jane Smith"
            />
          </div>
          <div className="form-field">
            <label htmlFor={`${idPrefix}-auth-phone`}>Phone</label>
            <input
              id={`${idPrefix}-auth-phone`} name="phone" type="tel" autoComplete="tel"
              value={form.phone} onChange={change} placeholder="021 123 4567"
            />
          </div>
        </>
      )}

      <div className="form-field">
        <label htmlFor={`${idPrefix}-auth-email`}>Email</label>
        <input
          id={`${idPrefix}-auth-email`} name="email" type="email" autoComplete="email"
          ref={emailRef} value={form.email} onChange={change} placeholder="you@email.com"
          aria-describedby={emailHint ? `${idPrefix}-auth-email-why` : undefined}
        />
        {emailHint && (
          <p className="bf-auth-hint" id={`${idPrefix}-auth-email-why`}>{emailHint}</p>
        )}
      </div>

      <div className="form-field">
        <label htmlFor={`${idPrefix}-auth-password`}>Password</label>
        <input
          id={`${idPrefix}-auth-password`} name="password" type="password"
          autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
          value={form.password} onChange={change}
        />
        {mode === 'signup' && <p className="bf-auth-hint">At least 8 characters.</p>}
      </div>

      {error && <p className="bf-auth-error" role="alert">{error}</p>}
      {notice && <p className="bf-auth-notice" role="status">{notice}</p>}

      <button type="submit" className="bf-auth-submit" disabled={busy}>
        {busy ? 'Just a moment…' : mode === 'signup' ? 'Create account' : 'Sign in'}
      </button>

      <div className="bf-auth-alt">
        {mode === 'signin' ? (
          <button type="button" className="bf-auth-link" onClick={() => onModeChange('signup')}>
            New here? Create an account
          </button>
        ) : (
          <button type="button" className="bf-auth-link" onClick={() => onModeChange('signin')}>
            Already have an account? Sign in
          </button>
        )}
      </div>
    </form>
  );
}

/* ── the gate ───────────────────────────────────────────────────────────── */
// The first and only thing on the page for anyone without an account. Three
// ways past it and all three are one tap: Google, email, or guest.
//
// It is shown on every visit rather than remembered, because "remembered" would
// mean a client who declined once is never asked again — and the money is worth
// asking about.
//
// The three routes are deliberately not equal any more. Google is filled and
// full width, email is a pair of outlined buttons under it, and guest is quiet
// and below a rule. None of that is a wall: guest is still a real button of
// full width and 46px, never disabled, never behind a "show more". What tells a
// guest what they are giving up is one factual sentence — there is nowhere to
// see this booking afterwards — rather than the button being made hard to find.

function SignInGate({ prefill, onGuest, onDetails, onPending, onBeforeGoogle, onGoogleFailed }) {
  const [mode, setMode] = useState('closed');
  const [googleError, setGoogleError] = useState('');

  return (
    <div className="bf-gate">
      <h2 className="bf-gate-title">Sign in and I&apos;ll remember you next time</h2>
      <p className="bf-gate-lead">
        An account is free and takes about a minute. Here's what it gets you.
      </p>

      <ul className="bf-gate-points">
        <li>
          <CalendarDays size={18} strokeWidth={1.9} aria-hidden="true" />
          <span>
            <strong>See what you've got booked.</strong> Cancel it yourself if life happens,
            no phone call needed.
          </span>
        </li>
        <li>
          <Gift size={18} strokeWidth={1.9} aria-hidden="true" />
          <span>
            <strong>I&apos;ll remember your birthday.</strong> Tell me when it is and I&apos;ll
            email you something for it, every year.
          </span>
        </li>
        <li>
          <Clock size={18} strokeWidth={1.9} aria-hidden="true" />
          <span>
            <strong>I&apos;ll remember your details.</strong> Your name, email and phone are
            filled in for you, and what you had last time is one tap away.
          </span>
        </li>
      </ul>

      <GoogleButton
        label="Sign in with Google"
        redirectPath="/book"
        variant="primary"
        onBeforeGoogle={onBeforeGoogle}
        onGoogleFailed={onGoogleFailed}
        onError={setGoogleError}
      />
      <p className="bf-gate-hint">
        Fastest way in — you&apos;ll come straight back here to finish booking.
      </p>

      {mode === 'closed' ? (
        <div className="bf-gate-secondary">
          <button type="button" className="bf-secondary-btn" onClick={() => setMode('signin')}>
            Sign in with email
          </button>
          <button type="button" className="bf-secondary-btn" onClick={() => setMode('signup')}>
            Create an account
          </button>
        </div>
      ) : (
        <>
          <div className="bf-or"><span>or</span></div>
          <EmailPasswordForm
            mode={mode}
            onModeChange={setMode}
            idPrefix="gate"
            prefill={prefill}
            emailHint={mode === 'signup'
              ? 'Use the email you book with and your past appointments will already be there.'
              : ''}
            onDetails={onDetails}
            onPending={onPending}
          />
        </>
      )}

      {googleError && <p className="bf-auth-error" role="alert">{googleError}</p>}

      <div className="bf-gate-guest">
        <p className="bf-gate-guest-text">
          Booking as a guest is completely fine — I&apos;ll see it either way. You just
          won&apos;t have anywhere to check it afterwards, and next time you'll be starting
          from scratch.
        </p>
        <button type="button" className="bf-gate-guest-btn" onClick={onGuest}>
          Continue as a guest
        </button>
      </div>
    </div>
  );
}

/* ── asking for a birthday ──────────────────────────────────────────────── */
// Nowhere else on the site asks, so until this existed the birthday email could
// never fire for anybody. It is asked here because the client is signed in and
// has nothing left to do on this screen — the cheapest moment there is to answer
// one more question.
//
// Day and month only. The year would tell us their age, which is not needed to
// email somebody in the right month, and is more than the ask is worth.
//
// Entirely skippable: it is a block under a finished booking, with no modal and
// no blocking, and closing the page is a perfectly good answer.

function BirthdayPrompt({ onSaved }) {
  const [day, setDay] = useState('');
  const [month, setMonth] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // Set only in the odd case below: a birthday that was already on the account
  // before this save, which the database has quietly kept.
  const [locked, setLocked] = useState(null);

  const maxDay = month ? DAYS_IN_MONTH[Number(month) - 1] : 31;

  // 31 selected, then February chosen: the option is about to disappear from the
  // list, so the choice goes with it rather than leaving the select showing a
  // day it no longer holds.
  const changeMonth = (value) => {
    setMonth(value);
    setError('');
    if (value && day && Number(day) > DAYS_IN_MONTH[Number(value) - 1]) setDay('');
  };

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;

    if (!day || !month) {
      setError('Please choose both a day and a month.');
      return;
    }

    setBusy(true);
    setError('');

    try {
      // The row that comes back is what the database actually holds, after the
      // trigger has had its say. That matters here: once a birthday is set the
      // trigger silently pins it to the old value rather than raising an error,
      // so trusting the request instead of the response would let this screen
      // say "saved" about a change that never happened. It only bites in a race
      // — the account page in another tab, a second device — but the whole point
      // of the message is that the client can rely on it.
      const updated = await supabaseService.updateMyProfile({
        birth_day: day, birth_month: month,
      });

      const savedDay = Number(updated?.birth_day) || null;
      const savedMonth = Number(updated?.birth_month) || null;

      if (savedDay === Number(day) && savedMonth === Number(month)) {
        // The parent takes this away and says thank you, so nothing is set here
        // — the component is on its way out.
        onSaved(savedDay, savedMonth);
        return;
      }

      if (savedDay && savedMonth) {
        setLocked({ day: savedDay, month: savedMonth });
        setBusy(false);
        return;
      }

      setError(
        "That didn't save. Have another go, or add your birthday on your account page.",
      );
      setBusy(false);
    } catch (err) {
      setError(err.message || "That didn't save. Please try again.");
      setBusy(false);
    }
  };

  // Somebody else — another tab, another device — got there first. Said plainly,
  // with the date that is actually on the account and the one way to change it,
  // rather than a thank-you for something that did not happen.
  if (locked) {
    return (
      <p className="bf-birthday-locked" role="status">
        <Gift size={17} strokeWidth={1.9} aria-hidden="true" />
        <span>
          Your birthday is already set to <strong>{locked.day} {MONTH_NAMES[locked.month - 1]}
          </strong>, and it can only be set once, so I&apos;ve left it as it is. Give me a call on{' '}
          <a href="tel:021881498">021 881 498</a> if that isn&apos;t right.
        </span>
      </p>
    );
  }

  return (
    <form className="bf-birthday" onSubmit={submit} noValidate>
      <p className="bf-birthday-title">
        <Gift size={17} strokeWidth={1.9} aria-hidden="true" />
        <span>When&apos;s your birthday?</span>
      </p>
      <p className="bf-birthday-text">
        I&apos;ll email you something for your birthday month, every year — my treat.
      </p>

      <div className="bf-birthday-row">
        <div className="bf-birthday-field">
          <label htmlFor="bf-birth-day">Day</label>
          <select
            id="bf-birth-day"
            value={day}
            onChange={(e) => { setDay(e.target.value); setError(''); }}
          >
            <option value="">Day</option>
            {Array.from({ length: maxDay }, (_, i) => i + 1).map(d => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
        </div>

        <div className="bf-birthday-field">
          <label htmlFor="bf-birth-month">Month</label>
          <select
            id="bf-birth-month"
            value={month}
            onChange={(e) => changeMonth(e.target.value)}
          >
            <option value="">Month</option>
            {MONTH_NAMES.map((name, i) => (
              <option key={name} value={i + 1}>{name}</option>
            ))}
          </select>
        </div>

        <button type="submit" className="bf-birthday-save" disabled={busy}>
          {busy ? 'Saving…' : 'Save'}
        </button>
      </div>

      {error && <p className="bf-auth-error" role="alert">{error}</p>}

      <p className="bf-birthday-note">
        I only need the day and month, never the year. It can only be set once, so have a quick
        look before you save.
      </p>
    </form>
  );
}

/* ── the same ask, one step earlier ─────────────────────────────────────── */
// The confirmation screen asks too, but only once the booking is made and only
// for as long as the client stays on the page. This catches the same gap while
// they are already filling boxes in — and if they skip it here, the ask on the
// confirmation screen still gets its turn.
//
// Birthday only, deliberately. The phone number is a required field a few lines
// above this one and is written back to the profile the moment the booking
// succeeds, so "I don't have your phone number" printed above a box they are
// already typing into would be asking twice for one answer.
//
// It lives inside the booking form, so it is a <div> rather than a <form> —
// forms cannot nest — and every button in it is type="button" so that nothing
// here can submit a booking early.

function BirthdayNudge({ onSaved, onDismiss }) {
  const [day, setDay] = useState('');
  const [month, setMonth] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // Set only in the odd case below: a birthday already on the account before
  // this save, which the database has quietly kept.
  const [locked, setLocked] = useState(null);

  const maxDay = month ? DAYS_IN_MONTH[Number(month) - 1] : 31;

  // 31 selected, then February chosen: the option is about to leave the list,
  // so the choice goes with it.
  const changeMonth = (value) => {
    setMonth(value);
    setError('');
    if (value && day && Number(day) > DAYS_IN_MONTH[Number(value) - 1]) setDay('');
  };

  const save = async () => {
    if (busy) return;

    if (!day || !month) {
      setError('Please choose both a day and a month.');
      return;
    }

    setBusy(true);
    setError('');

    try {
      // Same reason as the ask on the confirmation screen: once a birthday is
      // set, the trigger pins it to the old value rather than raising an error.
      // The row that comes back is the only thing worth believing, and telling
      // somebody their birthday is saved when it is not is worse than not
      // asking at all.
      const updated = await supabaseService.updateMyProfile({
        birth_day: day, birth_month: month,
      });

      const savedDay = Number(updated?.birth_day) || null;
      const savedMonth = Number(updated?.birth_month) || null;

      if (savedDay === Number(day) && savedMonth === Number(month)) {
        // The parent replaces this block with a one-line thank you, so nothing
        // is set here — the component is on its way out.
        onSaved(savedDay, savedMonth);
        return;
      }

      if (savedDay && savedMonth) {
        setLocked({ day: savedDay, month: savedMonth });
        setBusy(false);
        return;
      }

      setError("That didn't save. Have another go, or add your birthday on your account page.");
      setBusy(false);
    } catch (err) {
      setError(err.message || "That didn't save. Please try again.");
      setBusy(false);
    }
  };

  // Another tab or another device got there first. Said plainly, with the date
  // the account actually holds.
  if (locked) {
    return (
      <p className="bf-nudge-done" role="status">
        <Gift size={17} strokeWidth={1.9} aria-hidden="true" />
        <span>
          Your birthday is already set to{' '}
          <strong>{locked.day} {MONTH_NAMES[locked.month - 1]}</strong>, and it can only be set
          once, so I&apos;ve left it as it is. Give me a call on{' '}
          <a href="tel:021881498">021 881 498</a> if that isn&apos;t right.
        </span>
      </p>
    );
  }

  return (
    <div className="bf-nudge">
      <p className="bf-birthday-title">
        <Gift size={17} strokeWidth={1.9} aria-hidden="true" />
        <span>One thing I don&apos;t have — your birthday</span>
      </p>
      <p className="bf-birthday-text">
        Day and month, and I&apos;ll email you something for your birthday month, every year.
        Never the year you were born — I don&apos;t need it.
      </p>

      <div className="bf-birthday-row">
        <div className="bf-birthday-field">
          <label htmlFor="bf-nudge-day">Day</label>
          <select
            id="bf-nudge-day"
            value={day}
            onChange={(e) => { setDay(e.target.value); setError(''); }}
          >
            <option value="">Day</option>
            {Array.from({ length: maxDay }, (_, i) => i + 1).map(d => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
        </div>

        <div className="bf-birthday-field">
          <label htmlFor="bf-nudge-month">Month</label>
          <select
            id="bf-nudge-month"
            value={month}
            onChange={(e) => changeMonth(e.target.value)}
          >
            <option value="">Month</option>
            {MONTH_NAMES.map((name, i) => (
              <option key={name} value={i + 1}>{name}</option>
            ))}
          </select>
        </div>

        <button type="button" className="bf-birthday-save" onClick={save} disabled={busy}>
          {busy ? 'Saving…' : 'Save'}
        </button>
      </div>

      {error && <p className="bf-auth-error" role="alert">{error}</p>}

      <div className="bf-nudge-foot">
        <p className="bf-nudge-note">
          It can only be set once, so have a quick look before you save.
        </p>
        <button type="button" className="bf-nudge-skip" onClick={onDismiss}>
          Not now
        </button>
      </div>
    </div>
  );
}

/* ── the booking form ───────────────────────────────────────────────────── */

const BookingForm = () => {
  const [treatments, setTreatments] = useState({ all: [], grouped: {} });
  const [selectedTreatments, setSelectedTreatments] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    notes: '',
  });
  const [loading, setLoading] = useState(false);
  const [loadingTreatments, setLoadingTreatments] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const [error, setError] = useState('');
  const [expandedCategories, setExpandedCategories] = useState({});
  const [marketingConsent, setMarketingConsent] = useState(false);

  // Slot picker
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsError, setSlotsError] = useState('');
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [pageStart, setPageStart] = useState(() => new Date());
  // How far ahead she is taking bookings, as YYYY-MM-DD, straight from
  // booking_settings. Null until the first load answers, or if it could not be
  // read -- both cases fall back to the older, vaguer wording.
  const [lastBookableDate, setLastBookableDate] = useState(null);
  const [expandedDay, setExpandedDay] = useState(null);

  const [userId, setUserId] = useState(null);
  // The address of the account that is signed in, which is NOT the same thing as
  // the address in the email field -- that one is whoever the booking is for.
  // Kept apart on purpose: a device left signed in is the normal state, because
  // Supabase refreshes silently and a session from months ago is still live.
  const [accountEmail, setAccountEmail] = useState(null);

  // The gate. authReady exists so nobody is shown a gate for half a second on
  // their way past it — the session is read from storage before anything decides
  // what to render.
  const [authReady, setAuthReady] = useState(false);
  const [guestChosen, setGuestChosen] = useState(false);
  const [accountNotice, setAccountNotice] = useState('');

  const [profile, setProfile] = useState(null);
  // { day, month } once the birthday has been given — in the details step or on
  // the confirmation screen — so the ask can be replaced by an answer without
  // refetching the profile.
  const [birthdaySaved, setBirthdaySaved] = useState(null);
  // "Not now" on the prompt in the details step. Held here rather than inside
  // the prompt so that dismissing it survives the step re-rendering underneath
  // it, and so that changing the time doesn't bring it back.
  const [birthdayNudgeOff, setBirthdayNudgeOff] = useState(false);
  const [lastVisit, setLastVisit] = useState(null);
  // The review step between filling the form in and the booking actually being
  // made. Clients kept turning up at the wrong one of Viktoria's two places --
  // the address was on screen the whole time, unlabelled and in grey, and they
  // read straight past it. So the last thing before committing is a screen whose
  // largest element is where they are going, and which will not proceed until
  // they have said they have read it.
  const [reviewing, setReviewing] = useState(false);

  const [pickerOpen, setPickerOpen] = useState(true);
  const [bookingForSomeoneElse, setBookingForSomeoneElse] = useState(false);

  // Coming back from Google mid-booking.
  const [resume, setResume] = useState(null);
  const [resumeState, setResumeState] = useState('');

  const submittingRef = useRef(false);
  const resumeReadRef = useRef(false);
  const pendingSlotRef = useRef(null);
  const timeSectionRef = useRef(null);
  const detailsSectionRef = useRef(null);
  const formTopRef = useRef(null);
  const nameRef = useRef(null);
  const wasGatedRef = useRef(false);

  // False once somebody says "not you". A profile fetch that lands after that —
  // and it can, they are milliseconds apart — must not put the wrong email back.
  const ownDetailsRef = useRef(true);

  const totals = selectedTreatments.reduce(
    (acc, t) => {
      if (t.duration_minutes) acc.duration += t.duration_minutes;
      const priceNum = parseFloat((t.price || '').replace(/[^0-9.]/g, ''));
      if (!isNaN(priceNum)) acc.price += priceNum;
      else acc.hasPOA = true;
      if (!t.duration_minutes) acc.enquiryOnly = true;
      // MAX, not sum: a facial and a massage back to back still need one rest at
      // the end. This is Viktoria's recovery time — invisible to the client and
      // never charged for, but her diary has to hold it.
      acc.rest = Math.max(acc.rest, Number(t.rest_minutes) || 0);
      return acc;
    },
    { duration: 0, rest: 0, price: 0, hasPOA: false, enquiryOnly: false },
  );

  const canPickTime = selectedTreatments.length > 0 && !totals.enquiryOnly && totals.duration > 0;

  useEffect(() => {
    loadTreatments();

    // Guarded so a StrictMode double-run cannot read the resume twice — the
    // first read clears it.
    if (resumeReadRef.current) return;
    resumeReadRef.current = true;

    const saved = readResume();
    if (saved) {
      setResume(saved);
      setResumeState('restoring');
    } else {
      // A rebooking link and a half-finished booking cannot both be true; the
      // one in front of us wins.
      prefillFromRebookingLink();
    }
  }, []);

  // Auth is the one thing components read directly. Keyed on the user id rather
  // than the session object, so the hourly token refresh doesn't refetch.
  useEffect(() => {
    let active = true;

    supabase.auth.getSession()
      .then(({ data: { session } }) => {
        if (!active) return;
        setUserId(session?.user?.id ?? null);
        setAccountEmail(session?.user?.email ?? null);
        setAuthReady(true);
      })
      .catch((err) => {
        // Treat an unreadable session as signed out rather than stalling. The
        // gate's guest route works without a session, so the worst case is
        // someone signed in being asked to sign in again -- far better than a
        // booking page stuck on "One moment" with no way past it.
        console.error('Could not read your session:', err);
        if (active) setAuthReady(true);
      });

    // getSession takes a cross-tab lock and can wait indefinitely on another tab
    // that is mid-refresh. Nothing about that should stop somebody booking.
    const failsafe = setTimeout(() => {
      if (active) setAuthReady(true);
    }, 4000);

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!active) return;
      setUserId(session?.user?.id ?? null);
      setAccountEmail(session?.user?.email ?? null);
      setAuthReady(true);
    });

    return () => {
      active = false;
      clearTimeout(failsafe);
      subscription.unsubscribe();
    };
  }, []);

  // Whoever is signed in should not have to type their own name out again. What
  // they have already typed always wins, and so does having said "not you" —
  // Viktoria books clients from her own phone, and her address must never
  // reappear in a client's booking once she has cleared it.
  useEffect(() => {
    if (!userId) {
      setProfile(null);
      setBookingForSomeoneElse(false);
      ownDetailsRef.current = true;
      return undefined;
    }

    let active = true;
    supabaseService.getMyProfile()
      .then((data) => {
        if (!active || !data) return;
        setProfile(data);
        if (!ownDetailsRef.current) return;
        setFormData(prev => ({
          ...prev,
          name: prev.name || data.full_name || '',
          email: prev.email || data.email || '',
          phone: prev.phone || data.phone || '',
        }));
      })
      .catch((err) => {
        // Their details are a convenience. Failing to fetch them means a little
        // typing, not a broken booking, so it never becomes a message.
        console.error('Could not load your saved details:', err);
      });

    return () => { active = false; };
  }, [userId]);

  // Most people have the same thing every time. If we already know what that is,
  // the only decision left is when — so it is offered before the treatment list,
  // not after it. Fails quietly for the same reason the rewards balance does: a
  // shortcut that didn't load is a shortcut not offered, not an error.
  useEffect(() => {
    if (!userId) {
      setLastVisit(null);
      return undefined;
    }

    let active = true;
    supabaseService.getMyLastVisit()
      .then((data) => {
        if (active) setLastVisit(data || null);
      })
      .catch((err) => {
        console.error('Could not load your last visit:', err);
        if (active) setLastVisit(null);
      });

    return () => { active = false; };
  }, [userId]);

  // Which of this component's three full-screen states is showing. Named so the
  // thing that has to happen on every swap between them -- go back to the top,
  // and tell whoever is not looking at the screen what it is now -- lives in one
  // place instead of at each call site that happens to change the state.
  const screen = submitted ? 'confirmed' : reviewing ? 'review' : 'form';
  const prevScreenRef = useRef(screen);
  const screenHeadingRef = useRef(null);

  // Nothing scrolls a page for you when its content is replaced underneath it.
  // These three screens are wildly different heights and the button that swaps
  // them sits at the bottom of the one being replaced -- so "Book another" left
  // the client at an offset that only existed while the confirmation was there,
  // which on a phone is somewhere in the middle of the footer.
  //
  // An effect rather than the handler that sets the state, so the shorter DOM is
  // already committed. Target 0 rather than a ref, because 0 is the only offset
  // nothing downstream can invalidate -- and something always does here:
  // clearing the treatments empties the slot list a commit later and shortens
  // the page again underneath the animation.
  useEffect(() => {
    // Also skips StrictMode's second mount pass in development.
    if (prevScreenRef.current === screen) return;
    prevScreenRef.current = screen;

    window.scrollTo({ top: 0, behavior: 'smooth' });
    // Scrolling alone strands anyone not using a mouse: the button they pressed
    // no longer exists, so focus falls back to <body> and the next Tab starts
    // from the nav. preventScroll so this does not jump over the top of the
    // smooth scroll above.
    screenHeadingRef.current?.focus({ preventScroll: true });
  }, [screen]);

  // The gate is a full screen of its own, so whichever way they leave it — signed
  // in or as a guest — the form arrives with its first question at the top rather
  // than wherever the "continue" button happened to be.
  const gateShowing = authReady && !userId && !guestChosen && !submitted;

  useEffect(() => {
    if (gateShowing) {
      wasGatedRef.current = true;
      return;
    }
    if (!wasGatedRef.current) return;
    wasGatedRef.current = false;
    formTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [gateShowing]);

  const loadTreatments = async () => {
    try {
      setTreatments(await supabaseService.getTreatments());
    } catch (err) {
      console.error('Error loading treatments:', err);
      setError("The treatment list didn't load. Please refresh the page.");
    } finally {
      setLoadingTreatments(false);
    }
  };

  // The "book again" link in post-appointment emails. It now only prefills —
  // the client picks a real slot like anyone else, and it creates a new booking
  // rather than mutating the old one.
  const prefillFromRebookingLink = async () => {
    const id = new URLSearchParams(window.location.search).get('rebooking');
    if (!id) return;

    try {
      const data = await supabaseService.getRebookingData(id);
      if (!data) return;

      setFormData(prev => ({
        ...prev,
        name: prev.name || data.name || '',
        email: prev.email || data.email || '',
        phone: prev.phone || data.phone || '',
      }));

      if (data.booking_treatments) {
        setSelectedTreatments(data.booking_treatments.map(bt => ({
          id: bt.treatment_id,
          name: bt.treatment_name,
          duration_minutes: bt.duration_minutes,
          price: bt.price,
        })));
      }
    } catch (err) {
      console.error('Could not prefill from rebooking link:', err);
    }
  };

  const loadSlots = useCallback(async () => {
    if (!canPickTime) {
      setSlots([]);
      return;
    }

    setSlotsLoading(true);
    setSlotsError('');

    const end = new Date(pageStart);
    end.setDate(end.getDate() + DAYS_PER_PAGE - 1);

    try {
      const result = await supabaseService.getAvailableSlots({
        startDate: toISODate(pageStart),
        endDate: toISODate(end),
        durationMinutes: totals.duration,
        restMinutes: totals.rest,
      });
      setSlots(result.slots);
      // Only overwrite on a real answer. A null would silently drop the form
      // back to "nothing free in this fortnight" on the very pages the bound
      // exists to explain.
      if (result.lastBookableDate) setLastBookableDate(result.lastBookableDate);
    } catch (err) {
      console.error('Error loading slots:', err);
      setSlotsError(err.message);
      setSlots([]);
    } finally {
      setSlotsLoading(false);
    }
  }, [canPickTime, pageStart, totals.duration, totals.rest]);

  useEffect(() => {
    loadSlots();
  }, [loadSlots]);

  // A slot chosen for one treatment list may not fit another. The one exception
  // is a booking coming back from Google: the treatments have just been put back
  // and the time that goes with them is waiting to be put back too.
  useEffect(() => {
    const pending = pendingSlotRef.current;
    if (pending) {
      pendingSlotRef.current = null;
      setSelectedSlot(pending);
      setResumeState('checking');
      return;
    }
    setSelectedSlot(null);
  }, [totals.duration]);

  /* ── coming back from Google ──────────────────────────────────────────── */

  // Stage one: the treatments only exist as ids until the list has loaded.
  useEffect(() => {
    if (!resume || treatments.all.length === 0) return;

    const chosen = resume.treatmentIds
      .map(id => treatments.all.find(t => t.id === id))
      .filter(Boolean);

    setResume(null);

    if (chosen.length !== resume.treatmentIds.length) {
      // Something was taken off the menu, or made inactive, while they were
      // away. Putting back a partial booking silently would be worse than
      // asking, so nothing is restored.
      setResumeState('lost');
      return;
    }

    const duration = chosen.reduce((sum, t) => sum + (t.duration_minutes || 0), 0);

    setSelectedTreatments(chosen);
    setFormData(prev => ({ ...prev, notes: prev.notes || resume.notes || '' }));
    if (resume.marketingConsent) setMarketingConsent(true);

    if (duration > 0) {
      // Picked up by the duration effect once the new totals have rendered.
      pendingSlotRef.current = resume.slot;
    } else {
      setResumeState('lost');
    }
  }, [resume, treatments.all]);

  // Stage two: the slot was free when they left for Google. Somebody else may
  // have taken it in the meantime, and letting them press Confirm on a time that
  // no longer exists is a failed booking and a wasted trip through the form.
  useEffect(() => {
    if (resumeState !== 'checking' || !selectedSlot) return undefined;

    let active = true;
    const wanted = selectedSlot;

    (async () => {
      try {
        const fresh = await supabaseService.getAvailableSlots({
          startDate: wanted.slot_date,
          endDate: wanted.slot_date,
          durationMinutes: totals.duration,
          restMinutes: totals.rest,
        });
        if (!active) return;

        const match = fresh.slots.find(s => s.slot_time === wanted.slot_time);
        if (match) {
          setSelectedSlot(match);
          setResumeState('kept');
        } else {
          setSelectedSlot(null);
          setResumeState('gone');
          // Show the picker around the day they wanted, not the default
          // fortnight, so the nearest alternatives are the first thing they see.
          const wantedDay = new Date(`${wanted.slot_date}T00:00:00`);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          setPageStart(wantedDay > today ? wantedDay : today);
        }
      } catch (err) {
        // Availability is down, which is not the client's problem and not a
        // reason to throw away their time. Confirming re-checks it in the
        // database anyway, and says plainly if it has gone.
        console.error('Could not re-check the saved time:', err);
        if (active) setResumeState('kept');
      }
    })();

    return () => { active = false; };
  }, [resumeState, selectedSlot, totals.duration, totals.rest]);

  // Put them where the news is: back at their details if the time held, at the
  // picker if it didn't. Skipped while the gate is up — a client who came back
  // from Google without signing in is still looking at the gate, and scrolling
  // the page underneath it would only be confusing.
  useEffect(() => {
    if (gateShowing) return;
    if (resumeState === 'kept') {
      detailsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else if (resumeState === 'gone' || resumeState === 'lost') {
      timeSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [resumeState, gateShowing]);

  // Written immediately before the browser is handed to Google.
  const saveForGoogle = useCallback(() => {
    if (!selectedSlot || selectedTreatments.length === 0) return;
    writeResume({
      v: 1,
      savedAt: Date.now(),
      treatmentIds: selectedTreatments.map(t => t.id),
      slot: {
        slot_date: selectedSlot.slot_date,
        slot_time: selectedSlot.slot_time,
        location_name: selectedSlot.location_name || null,
        location_address: selectedSlot.location_address || null,
      },
      notes: formData.notes,
      marketingConsent,
    });
  }, [selectedSlot, selectedTreatments, formData.notes, marketingConsent]);

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({ ...prev, [category]: !prev[category] }));
  };

  const toggleTreatment = (treatment) => {
    setSelectedTreatments(prev =>
      prev.find(t => t.id === treatment.id)
        ? prev.filter(t => t.id !== treatment.id)
        : [...prev, treatment],
    );
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Anything typed into the three contact fields is theirs from then on, and
  // nothing arriving later gets to overwrite it.
  const fillEmptyDetails = (details) => {
    setFormData(prev => ({
      ...prev,
      name: prev.name || details.name || '',
      email: prev.email || details.email || '',
      phone: prev.phone || details.phone || '',
    }));
  };

  // What a repeat would actually be. Deliberately recomputed from the treatments
  // that came back rather than read off the old booking's totals: the database
  // drops anything since retired, so a visit that was three treatments may only
  // be repeatable as two, and the old total would quote a price for something
  // they are not being offered.
  const repeat = (() => {
    const items = lastVisit?.treatments || [];
    if (items.length === 0) return null;

    let duration = 0;
    let price = 0;
    let hasPOA = false;

    items.forEach((t) => {
      duration += t.duration_minutes || 0;
      const priceNum = parseFloat((t.price || '').replace(/[^0-9.]/g, ''));
      if (!isNaN(priceNum)) price += priceNum;
      else hasPOA = true;
    });

    if (duration <= 0) return null;
    return { items, duration, cents: Math.round(price * 100), hasPOA };
  })();

  // Not while a booking is being put back together after signing in, and not
  // once they have started choosing for themselves — an offer to repeat is only
  // a shortcut while there is nothing to overwrite.
  const showRepeat = Boolean(userId && repeat && selectedTreatments.length === 0 && !resume);

  const repeatLastVisit = () => {
    setSelectedTreatments(repeat.items.map(t => ({
      id: t.id,
      name: t.name,
      duration_minutes: t.duration_minutes,
      price: t.price,
    })));
    // The treatment list has just been answered for them, so it folds away —
    // with one button to open it again, because changing their mind has to stay
    // as easy as not changing it.
    setPickerOpen(false);
    window.requestAnimationFrame(() => {
      timeSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  // Choosing a time by hand answers whatever the restore had to tell them, so
  // the notice about it goes away rather than sitting there contradicting the
  // time now on screen.
  const chooseSlot = (slot) => {
    setSelectedSlot(slot);
    setResumeState('');
  };

  // Signed in, but the details on screen are not theirs — booking for a
  // daughter, a friend, or Viktoria booking a client from her own phone. All
  // three fields empty out and the caret goes where the typing has to start.
  const useOtherDetails = () => {
    ownDetailsRef.current = false;
    setBookingForSomeoneElse(true);
    setFormData(prev => ({ ...prev, name: '', email: '', phone: '' }));
    window.requestAnimationFrame(() => nameRef.current?.focus());
  };

  const useMyDetails = () => {
    ownDetailsRef.current = true;
    setBookingForSomeoneElse(false);
    // Their profile never loaded, so there is nothing to put back. Emptying the
    // fields instead would be the worst of both.
    if (!profile) return;
    setFormData(prev => ({
      ...prev,
      name: profile.full_name || prev.name,
      email: profile.email || prev.email,
      phone: profile.phone || prev.phone,
    }));
  };

  const slotsByDay = slots.reduce((acc, slot) => {
    (acc[slot.slot_date] = acc[slot.slot_date] || []).push(slot);
    return acc;
  }, {});
  const days = Object.keys(slotsByDay).sort();

  const shiftPage = (deltaDays) => {
    const next = new Date(pageStart);
    next.setDate(next.getDate() + deltaDays);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    setPageStart(next < today ? today : next);
    setExpandedDay(null);
  };

  const atStart = toISODate(pageStart) === toISODate(new Date());

  // True when every day on this page is past the last bookable date, which is
  // the case the empty state used to misreport as a full diary.
  const pastBookingWindow = !!lastBookableDate && toISODate(pageStart) > lastBookableDate;

  // Stop the forward arrow walking into dates that can never return anything.
  // It used to page on forever, each page repeating the same "look further
  // ahead" advice that had just failed.
  const atWindowEnd = !!lastBookableDate && (() => {
    const nextPage = new Date(pageStart);
    nextPage.setDate(nextPage.getDate() + DAYS_PER_PAGE);
    return toISODate(nextPage) > lastBookableDate;
  })();

  const identityName = (profile?.full_name || '').trim() || (formData.name || '').trim();

  // Viktoria works two places and which one depends on the weekday, so a regular
  // who always comes on a Monday and books a Wednesday is going somewhere else
  // without having decided to. That is the moment the mistake happens, and it is
  // the moment worth interrupting. Only claimed when both names are known and
  // actually differ -- a first-timer has nothing to compare against and must not
  // be told anything has "changed".
  const previousLocation = (lastVisit?.location_name || '').trim();
  const nextLocation = (selectedSlot?.location_name || '').trim();
  const locationChanged = Boolean(previousLocation && nextLocation && previousLocation !== nextLocation);

  // Quietly bring a signed-in client's profile up to date with the phone number
  // they just booked with. Never awaited, never surfaced: a failure here means
  // they get asked to type it again next time, which is exactly where they were
  // anyway.
  const savePhoneToProfile = () => {
    if (!userId || !ownDetailsRef.current || bookingForSomeoneElse) return;

    const phone = (formData.phone || '').trim();
    if (!phone) return;

    // Only ever fills a blank. The whole reason this exists is that Google never
    // supplies a phone number, so most accounts have none — and the banner above
    // the fields invites the client to "type over anything below to change it",
    // which the "not you" flag does not catch. Without this, Viktoria booking a
    // client from her own account writes their number onto her profile, and it
    // prefills on her next booking.
    if ((profile?.phone || '').trim()) return;

    // Identity check as well as a blank check: a rebooking link can legitimately
    // fill the form with someone else's details while signed in as another
    // person. profiles.email is pinned by a database trigger, so it is a
    // trustworthy key to compare against.
    const formEmail = (formData.email || '').trim().toLowerCase();
    const profileEmail = (profile?.email || '').trim().toLowerCase();
    if (!profileEmail || formEmail !== profileEmail) return;

    // The name is sent because updateMyProfile keeps auth metadata in step with
    // it; sending nothing would be indistinguishable from clearing it. Their
    // saved name wins over what is on the form — this is a phone number update,
    // not a rename.
    const fullName = (profile?.full_name || formData.name || '').trim();

    supabaseService.updateMyProfile({ full_name: fullName || undefined, phone })
      .then((updated) => {
        if (updated) setProfile(updated);
      })
      .catch((err) => {
        console.error('Could not save your phone number to your profile:', err);
      });
  };

  // The form's submit no longer books anything. It validates, then hands over to
  // the review step; confirmBooking below is what actually creates the booking.
  const handleSubmit = (e) => {
    e.preventDefault();
    if (submittingRef.current) return;

    setError('');

    if (selectedTreatments.length === 0) {
      setError('Please select at least one treatment.');
      return;
    }
    if (!selectedSlot) {
      setError('Please choose a time.');
      return;
    }

    setReviewing(true);
  };

  const confirmBooking = async () => {
    if (submittingRef.current) return;

    setError('');
    submittingRef.current = true;
    setLoading(true);

    try {
      const { id } = await supabaseService.createInstantBooking({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        date: selectedSlot.slot_date,
        time: selectedSlot.slot_time,
        treatmentIds: selectedTreatments.map(t => t.id),
        notes: formData.notes,
        marketingConsent,
      });

      setConfirmed({
        id,
        name: (formData.name || '').trim(),
        date: selectedSlot.slot_date,
        time: selectedSlot.slot_time,
        locationName: selectedSlot.location_name || null,
        location: selectedSlot.location_address,
        email: (formData.email || '').trim(),
        phone: (formData.phone || '').trim(),
      });
      setSubmitted(true);
      // The review step is over. Without this the guard at the top of the
      // render -- `reviewing && selectedSlot` -- still matches, the confirmation
      // underneath it is unreachable, and the client is left looking at "Check
      // your booking" with a live Confirm button over an appointment that
      // already exists. Pressing it again books a second one.
      setReviewing(false);

      // Nothing is in progress any more.
      clearResume();

      // Google hands over a name and an email and never a phone number, so most
      // accounts here have none at all — and the client has just typed one. Keep
      // it, so the next booking has it already filled in.
      //
      // Fire and forget, after the booking has succeeded: the appointment is
      // made and nothing about tidying a profile is allowed to endanger that. No
      // prompt and no confirmation either — the only visible effect is that we
      // stop asking. Skipped when they are booking for somebody else, because
      // that number is not theirs.
      savePhoneToProfile();

      // Confirmation email, calendar mirror and Viktoria's notification. Fired
      // without blocking the client — a reconciliation sweep picks up anything
      // lost if they close the tab.
      // Only the id — the edge function loads the booking and derives everything
      // from it. Sending the details from here meant a public endpoint would
      // email whatever it was handed, and meant a first-time client got the
      // returning-client wording because clientType was never included.
      supabaseService.sendNotification('booking_confirmed', { id })
        .catch(err => console.error('Confirmation notification failed:', err));
    } catch (err) {
      console.error('Error submitting booking:', err);
      setError(err.message);
      if (err.slotTaken) {
        setSelectedSlot(null);
        loadSlots();
        // The time they were reviewing is gone, so there is nothing left to
        // confirm. Back to the form, where the error and a fresh set of times
        // are waiting.
        setReviewing(false);
      }
    } finally {
      setLoading(false);
      submittingRef.current = false;
    }
  };

  const startAnotherBooking = () => {
    setSubmitted(false);
    setConfirmed(null);
    // Belonged to the booking just finished. Left set, the "that time went while
    // you were signing in" banner sits on top of the fresh one until they happen
    // to pick a time.
    setResumeState('');
    setBirthdaySaved(null);
    setSelectedTreatments([]);
    setSelectedSlot(null);
    setPickerOpen(true);
    setError('');
    // Their own details go straight back in if they are signed in and this is
    // their own booking. A signed-in client should never be handed three empty
    // fields they have already filled once today.
    setFormData({
      name: '',
      email: '',
      phone: '',
      notes: '',
      ...(profile && ownDetailsRef.current
        ? {
          name: profile.full_name || '',
          email: profile.email || '',
          phone: profile.phone || '',
        }
        : {}),
    });
  };

  /* ── the confirmation screen ──────────────────────────────────────────── */
  // The appointment first, always: the only thing they need from this screen is
  // proof that the right day and time went in. Everything else sits under it.

  // Only for a signed-in client whose profile has no birthday yet. A guest is
  // offered an account instead — there is nowhere to put a birthday without one
  // — and anyone who has already given theirs is never asked again, because the
  // database locks it after the first save.
  const askForBirthday = Boolean(
    userId && profile && !profile.birth_day && !profile.birth_month && !profile.birthday_locked,
  );

  // The same ask in the details step, for anyone who might close the tab before
  // the confirmation screen gets its turn. Not shown while they are booking for
  // somebody else: "your birthday" is ambiguous about whose the moment the name
  // on the form isn't theirs, and Viktoria books clients from her own phone.
  const showBirthdayNudge = askForBirthday && !bookingForSomeoneElse && !birthdayNudgeOff;

  // Whichever of the two asks got the answer, the effect is the same: the other
  // one stops asking, and the confirmation screen says thank you.
  const keepBirthday = (day, month) => {
    setBirthdaySaved({ day, month });
    setProfile(prev => (prev ? { ...prev, birth_day: day, birth_month: month } : prev));
  };

  // ── Confirm your booking ──────────────────────────────────────────────────
  //
  // The last thing before the booking is made, and the only screen on which
  // WHERE is the biggest element. Everything else here they already chose and
  // will recognise; the location is the one thing they have not been reading,
  // so it gets the size, the border and a tick box that holds the button shut
  // until they have said they have seen it.
  // `!submitted` is load-bearing, not belt and braces. This guard sits above the
  // confirmation screen, so while it matches, that screen cannot render. Shipped
  // in 949b484 without it, and confirmBooking did not clear `reviewing` on
  // success -- so a client pressed Confirm, the booking was created, the screen
  // did not change, and pressing the still-live button again made a second one.
  // confirmBooking clears the flag now; this makes forgetting it again cost a
  // stale screen at worst rather than a duplicate appointment.
  if (reviewing && selectedSlot && !submitted) {
    return (
      <div className="booking-form-container">
        <div className="bf-review">
          <h2 className="bf-review-title" ref={screenHeadingRef} tabIndex={-1}>Check your booking</h2>
          <p className="bf-review-lead">Nothing is booked yet. Have a look, then confirm.</p>

          <div className="bf-review-rows">
            <div className="bf-review-row">
              <span className="bf-review-label">Treatments</span>
              <span className="bf-review-value">
                {selectedTreatments.map(t => t.name).join(', ')}
                {totals.duration ? <em> · {totals.duration} min</em> : null}
              </span>
            </div>
            <div className="bf-review-row">
              <span className="bf-review-label">When</span>
              <span className="bf-review-value">
                {formatDayLabel(selectedSlot.slot_date)} at {formatSlotTime(selectedSlot.slot_time)}
              </span>
            </div>
            <div className="bf-review-row">
              <span className="bf-review-label">Name</span>
              <span className="bf-review-value">{(formData.name || '').trim()}</span>
            </div>
          </div>

          {/* The whole reason this screen exists. */}
          <div className={`bf-where${locationChanged ? ' changed' : ''}`}>
            <span className="bf-where-label">Where</span>

            {locationChanged && (
              <p className="bf-where-changed" role="alert">
                This is <strong>not</strong> where you came last time. You were at{' '}
                {previousLocation}.
              </p>
            )}

            <strong className="bf-where-name">
              {selectedSlot.location_name || 'Location'}
            </strong>
            {selectedSlot.location_address && (
              <span className="bf-where-address">{selectedSlot.location_address}</span>
            )}

          </div>

          {error && <div className="form-error">{error}</div>}

          <div className="bf-review-actions">
            <button
              type="button"
              className="bf-review-back"
              onClick={() => setReviewing(false)}
              disabled={loading}
            >
              Go back
            </button>
            <button
              type="button"
              className="submit-btn"
              onClick={confirmBooking}
              disabled={loading}
            >
              {loading ? 'Confirming...' : 'Confirm booking'}
            </button>
          </div>

        </div>
      </div>
    );
  }

  if (submitted && confirmed) {
    return (
      <div className="booking-form-container">
        <div className="booking-success">
          <div className="success-icon">&#10003;</div>
          <h2 ref={screenHeadingRef} tabIndex={-1}>You're booked in!</h2>
          <p>Thanks {confirmed.name} — your appointment is confirmed.</p>
          <div className="booking-success-when">
            {formatDayLabel(confirmed.date)}<br />
            <strong>{formatSlotTime(confirmed.time)}</strong>
          </div>
          {(confirmed.locationName || confirmed.location) && (
            <p className="booking-success-where">
              {confirmed.locationName && <strong>{confirmed.locationName}</strong>}
              {confirmed.locationName && confirmed.location ? <br /> : null}
              {confirmed.location}
            </p>
          )}

          {/* The one place on the whole site a birthday can be given. Under the
              appointment, because it is the smallest thing on this screen and
              the appointment is the only thing they came for. */}
          {birthdaySaved ? (
            <p className="booking-success-birthday" role="status">
              <Gift size={17} strokeWidth={1.9} aria-hidden="true" />
              <span>
                Thanks — {birthdaySaved.day} {MONTH_NAMES[birthdaySaved.month - 1]} is on your
                account. I&apos;ll email you something for your birthday every{' '}
                {MONTH_NAMES[birthdaySaved.month - 1]}.
              </span>
            </p>
          ) : askForBirthday ? (
            <div className="booking-success-ask">
              <BirthdayPrompt onSaved={keepBirthday} />
            </div>
          ) : null}

          <p>
            A confirmation email is on its way. If you need to change anything, give me a call on
            021 881 498.
          </p>
          <button className="new-booking-btn" onClick={startAnotherBooking}>
            Book another appointment
          </button>
        </div>
      </div>
    );
  }

  /* ── the gate ─────────────────────────────────────────────────────────── */

  if (!authReady) {
    return (
      <div className="booking-form-container">
        <div className="booking-form-custom">
          <p className="loading-text">One moment…</p>
        </div>
      </div>
    );
  }

  if (gateShowing) {
    return (
      <div className="booking-form-container">
        <SignInGate
          prefill={{ name: formData.name, email: formData.email, phone: formData.phone }}
          onGuest={() => setGuestChosen(true)}
          onDetails={fillEmptyDetails}
          onPending={(message) => {
            setAccountNotice(message);
            setGuestChosen(true);
          }}
          onBeforeGoogle={saveForGoogle}
          onGoogleFailed={clearResume}
        />
      </div>
    );
  }

  return (
    <div className="booking-form-container">
      <form onSubmit={handleSubmit} className="booking-form-custom" ref={formTopRef}>
        <h2 ref={screenHeadingRef} tabIndex={-1}>Book an Appointment</h2>

        {accountNotice && (
          <div className="bf-resume" role="status">{accountNotice}</div>
        )}

        {/* Treatments */}
        <div className="form-section">
          <h3>1. Choose your treatment{selectedTreatments.length !== 1 ? 's' : ''}</h3>

          {/* The shortcut, before the list rather than after it. Two taps from
              done: this, then a time. "or choose below" is right next to it, so
              nobody has to undo anything to ignore it. */}
          {showRepeat && (
            <div className="bf-repeat">
              <p className="bf-repeat-title">Same as last time?</p>
              <p className="bf-repeat-treatments">
                {repeat.items.map(t => t.name).join(' · ')}
              </p>
              <p className="bf-repeat-meta">
                {repeat.duration} min · {formatMoney(repeat.cents)}{repeat.hasPOA ? '+' : ''}
              </p>
              {formatVisitDate(lastVisit.date) && (
                <p className="bf-repeat-when">Last visit {formatVisitDate(lastVisit.date)}</p>
              )}
              <div className="bf-repeat-actions">
                <button type="button" className="bf-repeat-btn" onClick={repeatLastVisit}>
                  Yes, book this again
                </button>
                <span className="bf-repeat-or">or choose below</span>
              </div>
            </div>
          )}

          {loadingTreatments ? (
            <p className="loading-text">Loading treatments...</p>
          ) : !pickerOpen ? null : (
            <div className="treatment-selector">
              {Object.entries(treatments.grouped).map(([category, items]) => (
                <div key={category} className="treatment-category">
                  <button
                    type="button"
                    className={`category-toggle ${expandedCategories[category] ? 'expanded' : ''}`}
                    onClick={() => toggleCategory(category)}
                  >
                    <span>{category}</span>
                    {expandedCategories[category]
                      ? <ChevronUp size={16} strokeWidth={2} aria-hidden="true" />
                      : <ChevronDown size={16} strokeWidth={2} aria-hidden="true" />}
                  </button>
                  {expandedCategories[category] && (
                    <div className="treatment-items">
                      {items.map(treatment => {
                        const isSelected = selectedTreatments.some(t => t.id === treatment.id);
                        return (
                          <label
                            key={treatment.id}
                            className={`treatment-item ${isSelected ? 'selected' : ''}`}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleTreatment(treatment)}
                            />
                            <div className="treatment-info">
                              <span className="treatment-name">{treatment.name}</span>
                              <span className="treatment-meta">
                                {treatment.duration_minutes
                                  ? `${treatment.duration_minutes} min | ${treatment.price}`
                                  : `${treatment.price} | by consultation`}
                              </span>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {selectedTreatments.length > 0 && (
            <div className="selected-summary">
              <h4>Selected ({selectedTreatments.length}):</h4>
              <ul>
                {selectedTreatments.map(t => (
                  <li key={t.id}>
                    {t.name}
                    <button
                      type="button"
                      className="remove-treatment"
                      onClick={() => toggleTreatment(t)}
                    >
                      &times;
                    </button>
                  </li>
                ))}
              </ul>
              <div className="totals">
                {totals.duration > 0 && <span>Total time: ~{totals.duration} min</span>}
                <span>Price: {totals.hasPOA ? `$${totals.price}+` : `$${totals.price}`}</span>
              </div>
            </div>
          )}

          {!loadingTreatments && !pickerOpen && (
            <button
              type="button"
              className="bf-picker-open"
              onClick={() => setPickerOpen(true)}
            >
              Choose different treatments
            </button>
          )}
        </div>

        {/* Time */}
        <div className="form-section" ref={timeSectionRef}>
          <h3>2. Pick a time</h3>

          {/* Back from Google and the time has gone. Said plainly and before the
              picker, because the alternative is a client pressing Confirm on
              something that cannot be booked. */}
          {resumeState === 'gone' && (
            <div className="bf-resume bf-resume--warn" role="status">
              <strong>That time went while you were signing in.</strong> Someone else booked it —
              nothing has been charged and nothing is held. Your treatments are still selected, so
              just pick another time below.
            </div>
          )}

          {resumeState === 'lost' && (
            <div className="bf-resume bf-resume--warn" role="status">
              <strong>Your booking didn't come back.</strong> One of the treatments you'd chosen
              isn't available any more. You're signed in now — choose again and your details will
              be filled in for you.
            </div>
          )}

          {selectedTreatments.length === 0 && (
            <p className="helper-text">Choose a treatment above and I'll show you what's free.</p>
          )}

          {totals.enquiryOnly && (
            <div className="enquiry-notice">
              <p>
                <strong>These treatments are arranged by consultation.</strong> They need a chat
                first so I can talk through what you're after and set aside the right amount of
                time for you.
              </p>
              <p>
                Give me a call on <a href="tel:021881498">021 881 498</a> or email{' '}
                <a href="mailto:viktoriashouseofbeauty@gmail.com">viktoriashouseofbeauty@gmail.com</a>{' '}
                and I'll get you sorted.
              </p>
            </div>
          )}

          {canPickTime && (
            <>
              <div className="slot-nav">
                <button
                  type="button"
                  className="slot-nav-btn"
                  onClick={() => shiftPage(-DAYS_PER_PAGE)}
                  disabled={atStart || slotsLoading}
                  aria-label="Earlier dates"
                >
                  <ChevronLeft size={18} strokeWidth={2} />
                </button>
                <span className="slot-nav-label">
                  {atStart ? 'Next 2 weeks' : `From ${formatDayLabel(toISODate(pageStart))}`}
                </span>
                <button
                  type="button"
                  className="slot-nav-btn"
                  onClick={() => shiftPage(DAYS_PER_PAGE)}
                  disabled={slotsLoading || atWindowEnd}
                  aria-label="Later dates"
                >
                  <ChevronRight size={18} strokeWidth={2} />
                </button>
              </div>

              {slotsLoading && <p className="loading-text">Finding available times...</p>}

              {!slotsLoading && slotsError && (
                <div className="slot-error">
                  <p>{slotsError}</p>
                  <button type="button" className="slot-retry" onClick={loadSlots}>Try again</button>
                </div>
              )}

              {/* Two very different empty states. Past the booking window the
                  diary is not full, it is simply not open yet -- and telling
                  someone to look further ahead there is advice that cannot work,
                  because every later page says the same thing. */}
              {!slotsLoading && !slotsError && days.length === 0 && pastBookingWindow && (
                <p className="helper-text">
                  I'm taking bookings up to {formatDayLabel(lastBookableDate)} at the moment.
                  For anything later, give me a call on <a href="tel:021881498">021 881 498</a>{' '}
                  and I'll get you in the book.
                </p>
              )}

              {!slotsLoading && !slotsError && days.length === 0 && !pastBookingWindow && (
                <p className="helper-text">
                  Nothing free in this fortnight for a {totals.duration} minute appointment.
                  {atWindowEnd
                    ? " That's as far ahead as I'm booking at the moment — give me a call on 021 881 498 for later dates."
                    : ' Try the arrow above to look further ahead.'}
                </p>
              )}

              {!slotsLoading && !slotsError && days.map(day => {
                const daySlots = slotsByDay[day];
                const isOpen = expandedDay === day || days.length <= 3;
                return (
                  <div key={day} className="slot-day">
                    <button
                      type="button"
                      className={`slot-day-header ${isOpen ? 'expanded' : ''}`}
                      onClick={() => setExpandedDay(isOpen && expandedDay === day ? null : day)}
                    >
                      <span className="slot-day-name">{formatDayLabel(day)}</span>
                      <span className="slot-day-count">
                        {daySlots.length} time{daySlots.length !== 1 ? 's' : ''}
                      </span>
                    </button>
                    {isOpen && (
                      <>
                        {daySlots[0].location_name && (
                          <p className="slot-day-location">
                            <span className="slot-day-location-label">Where</span>
                            <strong>{daySlots[0].location_name}</strong>
                            {daySlots[0].location_address && (
                              <span className="slot-day-location-address">
                                {daySlots[0].location_address}
                              </span>
                            )}
                          </p>
                        )}
                        <div className="slot-grid">
                          {daySlots.map(slot => {
                            const isSelected =
                              selectedSlot?.slot_date === slot.slot_date &&
                              selectedSlot?.slot_time === slot.slot_time;
                            return (
                              <button
                                key={`${slot.slot_date}-${slot.slot_time}`}
                                type="button"
                                className={`slot-btn ${isSelected ? 'selected' : ''}`}
                                onClick={() => chooseSlot(slot)}
                              >
                                {formatSlotTime(slot.slot_time)}
                              </button>
                            );
                          })}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </>
          )}
        </div>

        {/* Details */}
        {selectedSlot && (
          <>
            <div className="form-section" ref={detailsSectionRef}>
              <h3>3. Your details</h3>
              <div className="chosen-slot">
                <span className="chosen-slot-label">Your appointment</span>
                <strong>{formatDayLabel(selectedSlot.slot_date)} at {formatSlotTime(selectedSlot.slot_time)}</strong>
                {selectedSlot.location_name && (
                  <span className="chosen-slot-where">
                    <strong>{selectedSlot.location_name}</strong>
                    {selectedSlot.location_address ? ` — ${selectedSlot.location_address}` : ''}
                  </span>
                )}
              </div>

              {resumeState === 'kept' && (
                <div className="bf-resume" role="status">
                  <strong>Welcome back — your time is still here.</strong> Same treatments, same
                  time. Have a quick look at your details and you're done.
                </div>
              )}

              {/* Signed in: say whose booking this is, and give the way out its
                  own button rather than a word in a sentence. Viktoria books
                  clients from her own phone, so this is the control she uses
                  every time — it has to be the obvious thing on the screen. The
                  fields below stay editable in every state. */}
              {userId && (
                <div className="bf-identity" role="status">
                  {bookingForSomeoneElse ? (
                    <>
                      <p className="bf-identity-text">
                        Booking for someone else, so this appointment will belong to them and
                        not to <strong>{accountEmail || 'this account'}</strong>. They'll be
                        able to see it once they make an account with the email you enter below.
                      </p>
                      <button type="button" className="bf-identity-btn" onClick={useMyDetails}>
                        Use my own details
                      </button>
                    </>
                  ) : (
                    <>
                      {/* The account, not just the name. A device left signed in
                          is the normal state -- Supabase refreshes silently, so a
                          session from months ago is still live -- and on
                          2026-08-24 a client booked on a phone still signed in as
                          Viktoria. The name alone said nothing about that; the
                          address does. */}
                      <p className="bf-identity-text">
                        Booking as <strong>{identityName || 'you'}</strong>
                        {accountEmail ? <>, signed in as <strong>{accountEmail}</strong></> : null}.
                        Type over anything below to change it.
                      </p>
                      <button type="button" className="bf-identity-btn" onClick={useOtherDetails}>
                        Not you? Clear these fields
                      </button>
                    </>
                  )}
                </div>
              )}

              <div className="form-row">
                <div className="form-field">
                  <label htmlFor="name">Full Name *</label>
                  <input type="text" id="name" name="name" ref={nameRef} value={formData.name}
                    onChange={handleChange} required placeholder="Your full name"
                    autoComplete="name" />
                </div>
              </div>
              <div className="form-row two-col">
                <div className="form-field">
                  <label htmlFor="email">Email *</label>
                  <input type="email" id="email" name="email" value={formData.email}
                    onChange={handleChange} required placeholder="your@email.com"
                    autoComplete="email" />
                </div>
                <div className="form-field">
                  <label htmlFor="phone">Phone *</label>
                  <input type="tel" id="phone" name="phone" value={formData.phone}
                    onChange={handleChange} required placeholder="021 123 4567"
                    autoComplete="tel" />
                </div>
              </div>
              <div className="form-field">
                <label htmlFor="notes">Anything I should know?</label>
                <textarea id="notes" name="notes" value={formData.notes} onChange={handleChange}
                  rows="3" placeholder="Any allergies, preferences, or special requests..." />
              </div>

              {/* Last thing in the step, under the fields it is not part of:
                  the booking is what they came for, and this is a small extra
                  they can ignore. Answered here, the ask on the confirmation
                  screen turns into the same thank-you line instead. */}
              {birthdaySaved ? (
                <p className="bf-nudge-done" role="status">
                  <Gift size={17} strokeWidth={1.9} aria-hidden="true" />
                  <span>
                    Thanks — {birthdaySaved.day} {MONTH_NAMES[birthdaySaved.month - 1]} is on your
                    account. I&apos;ll email you something for your birthday every{' '}
                    {MONTH_NAMES[birthdaySaved.month - 1]}.
                  </span>
                </p>
              ) : showBirthdayNudge ? (
                <BirthdayNudge
                  onSaved={keepBirthday}
                  onDismiss={() => setBirthdayNudgeOff(true)}
                />
              ) : null}
            </div>

            <div className="cancellation-notice">
              <strong>Cancellation Policy:</strong> Please give me 24 hours' notice if you need to
              cancel, otherwise a 50% fee may apply.
            </div>

            <div className="form-section consent-section">
              <p className="privacy-notice">
                I use your details to take your booking and to get in touch about your
                appointment. See the <Link to="/privacy">Privacy Policy</Link> for more.
              </p>
              <label className="consent-checkbox">
                <input type="checkbox" checked={marketingConsent}
                  onChange={(e) => setMarketingConsent(e.target.checked)} />
                <span>I'd like to receive discounts and promotions from Beauty by Viktoria</span>
              </label>
            </div>

            {error && <div className="form-error">{error}</div>}

            <button type="submit" className="submit-btn" disabled={loading}>
              Check and confirm
            </button>
          </>
        )}

        {!selectedSlot && error && <div className="form-error">{error}</div>}
      </form>
    </div>
  );
};

export default BookingForm;
